/**
 * Winkelwagen met Section Rendering API.
 *
 * Opgezet naar het model van Shopify's eigen Hydrogen-thema
 * (templates/skeleton/app/components):
 *
 *   Hydrogen                              hier
 *   ------------------------------------  -------------------------------------
 *   <Aside type="cart">                   <cart-drawer> + sections/cart-drawer.liquid
 *   <CartMain layout="page" | "aside">    snippets/cart-contents.liquid
 *   <CartLineItem>                        snippets/cart-line-item.liquid
 *   <CartSummary>                         snippets/cart-summary.liquid
 *   CartForm ACTIONS.LinesAdd             HenkCart.addLines() / addFromForm()
 *   CartForm ACTIONS.LinesUpdate          HenkCart.updateLines()
 *   CartForm ACTIONS.LinesRemove          HenkCart.removeLines()
 *   useOptimisticCart()                   optimistische line-state (data-busy)
 *   open('cart') na add to cart           HenkCart.open()
 *
 * Iedere mutatie stuurt `sections=<ids>` mee, zodat Shopify de nieuwe HTML van
 * de drawer, het cart-icoon en (op /cart) de cart-sectie direct meelevert. Er
 * wordt dus nooit meer herladen of naar /cart genavigeerd.
 *
 * Publieke events op `document`:
 *   cart:updated  detail: { cart, source }
 *   cart:error    detail: { error, source }
 */
(() => {
  const CONFIG = readConfig();
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

  const URLS = {
    cart: `${ROOT}cart.js`,
    add: `${ROOT}cart/add.js`,
    change: `${ROOT}cart/change.js`,
    update: `${ROOT}cart/update.js`,
  };

  function readConfig() {
    const el = document.querySelector("[data-cart-config]");
    const defaults = {
      deliveryWeekEndpoint: "",
      addingMinDuration: 2500,
      strings: {
        deliveryLabel: "Verwachte levertijd",
        deliveryLoading: "Verwachte levertijd laden…",
        deliveryUnknown: "Verwachte levertijd onbekend",
        leadImmediate: "2-3 werkdagen",
        unitDay: "dag",
        unitDays: "dagen",
        unitWeek: "week",
        unitWeeks: "weken",
        error: "Er ging iets mis. Probeer het opnieuw.",
      },
    };
    if (!el) return defaults;
    try {
      const parsed = JSON.parse(el.textContent);
      return { ...defaults, ...parsed, strings: { ...defaults.strings, ...(parsed.strings || {}) } };
    } catch {
      return defaults;
    }
  }

  /* ----------------------------------------------------------------------- */
  /* Section rendering                                                        */
  /* ----------------------------------------------------------------------- */

  /**
   * De secties die na een cart-mutatie ververst moeten worden. Dit zijn alle
   * elementen die zichzelf als cart-sectie markeren: de drawer, het cart-icoon
   * in de header en — op /cart — de cart-sectie zelf.
   */
  function sectionIds() {
    return window.HenkSections
      ? window.HenkSections.ids("[data-cart-section][data-section-render]")
      : [];
  }

  function sectionsUrl() {
    return window.location.pathname + window.location.search;
  }

  function applySections(data) {
    if (data && data.sections && window.HenkSections) {
      window.HenkSections.apply(data.sections);
    }
  }

  /* ----------------------------------------------------------------------- */
  /* Requests                                                                 */
  /* ----------------------------------------------------------------------- */

  let inflight = 0;

  const isDrawerOpen = () =>
    document.querySelector("cart-drawer")?.isOpen === true;

  function setBusy(busy) {
    inflight = Math.max(0, inflight + (busy ? 1 : -1));
    document.documentElement.toggleAttribute("data-cart-busy", inflight > 0);
  }

  async function post(url, payload, source, { silent = false } = {}) {
    const ids = sectionIds();
    let body;
    const headers = { Accept: "application/json" };

    if (payload instanceof FormData) {
      payload.set("sections", ids.join(","));
      payload.set("sections_url", sectionsUrl());
      body = payload;
    } else {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify({
        ...payload,
        sections: ids.join(","),
        sections_url: sectionsUrl(),
      });
    }

    setBusy(true);
    try {
      const res = await fetch(url, { method: "POST", headers, body });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const message = data.description || data.message || `${res.status}`;
        throw Object.assign(new Error(message), { cartResponse: data });
      }

      applySections(data);
      document.dispatchEvent(
        new CustomEvent("cart:updated", { detail: { cart: data, source } }),
      );
      return data;
    } catch (error) {
      console.error("[henk-cart]", source, error);
      document.dispatchEvent(
        new CustomEvent("cart:error", { detail: { error, source } }),
      );
      // De levertijd-ophaler regelt zijn eigen inline foutmelding op de regel;
      // die hoeft niet als banner over de hele winkelwagen.
      if (!silent) showError(error.message);
      throw error;
    } finally {
      setBusy(false);
    }
  }

  // Op /cart bestaan er twee foutmelding-plekken (de pagina en de drawer);
  // welke van de twee zichtbaar is hangt ervan af of de drawer open staat.
  function showError(message) {
    document.querySelectorAll("[data-cart-error]").forEach((holder) => {
      holder.textContent = message || CONFIG.strings.error;
      holder.hidden = false;
    });
  }

  function clearError() {
    document.querySelectorAll("[data-cart-error]").forEach((holder) => {
      holder.textContent = "";
      holder.hidden = true;
    });
  }

  /* ----------------------------------------------------------------------- */
  /* Optimistische line-state (Hydrogen: isOptimistic)                        */
  /* ----------------------------------------------------------------------- */

  function lineByKey(key) {
    return document.querySelector(`[data-cart-line][data-key="${CSS.escape(key)}"]`);
  }

  function setLineBusy(key, busy) {
    document
      .querySelectorAll(`[data-cart-line][data-key="${CSS.escape(key)}"]`)
      .forEach((el) => el.toggleAttribute("data-busy", busy));
  }

  function setOptimisticQuantity(key, quantity) {
    document
      .querySelectorAll(`[data-cart-line][data-key="${CSS.escape(key)}"]`)
      .forEach((el) => {
        el.dataset.quantity = String(quantity);
        const display = el.querySelector("[data-cart-quantity-display]");
        if (display) display.textContent = String(quantity);
      });
  }

  /* ----------------------------------------------------------------------- */
  /* Laadstatus na add to cart                                                */
  /* ----------------------------------------------------------------------- */

  /**
   * De drawer gaat meteen bij de klik open, nog voor /cart/add.js antwoordt.
   * Zolang de request loopt staat `data-cart-adding` op <cart-drawer>: de
   * bestaande regels dimmen weg en er komt een voortgangsbalk met tekst
   * overheen (markup in sections/cart-drawer.liquid, styling in
   * assets/henk-cart-drawer.css).
   *
   * De balk blijft minimaal `addingMinDuration` staan. Dat is bewust: de
   * configurator-add duurt onvoorspelbaar lang, en een balk die na 200 ms
   * wegflitst voelt rommeliger dan eentje die even doorloopt.
   *
   * Zowel <cart-drawer> als de overlay staan buiten het swap-target van de
   * Section Rendering API, dus de state en de animatie overleven de render
   * die halverwege binnenkomt.
   *
   * Flows die eerst nog werk doen voordat ze /cart/add.js aanroepen — de
   * configurator maakt eerst een product aan bij de Configurator Creator en
   * maakt een screenshot — beginnen zelf met `HenkCart.beginAdding()`. De
   * sessie die daarmee start wordt hergebruikt door de `add()` die erna komt,
   * zodat de balk niet halverwege terugspringt naar nul.
   */
  const ADDING_MIN_DURATION = Math.max(0, Number(CONFIG.addingMinDuration) || 0);
  const ADDING_DONE_DURATION = 250; // gelijk aan de `cart-adding-complete` animatie

  let addingSession = 0;
  let activeAdding = null;

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  /**
   * Start (of hervat) de laadstatus. `openedDrawer` vertelt of de drawer door
   * deze sessie geopend is; bij een fout vóór de add gaat hij dan weer dicht,
   * zodat een mislukte poging geen lege drawer achterlaat.
   */
  function startAdding({ openedDrawer = false } = {}) {
    const drawer = document.querySelector("cart-drawer");
    if (!drawer || ADDING_MIN_DURATION <= 0) return null;

    // Loopt er al een sessie (beginAdding() vóór een trage servercall), dan
    // neemt de add die erna komt dezelfde balk over in plaats van opnieuw te
    // beginnen.
    if (activeAdding) return activeAdding;

    // Twee adds vlak na elkaar: alleen de laatste mag de state opruimen.
    const session = ++addingSession;
    const startedAt = Date.now();
    let claimed = false;

    drawer.style.setProperty("--cart-adding-duration", `${ADDING_MIN_DURATION}ms`);
    drawer.setAttribute("data-cart-adding", "loading");

    function clear() {
      if (session !== addingSession) return;
      activeAdding = null;
      drawer.removeAttribute("data-cart-adding");
      drawer.style.removeProperty("--cart-adding-duration");
    }

    const handle = {
      /** Maakt de balk af nadat de minimumduur verstreken is. */
      async finish() {
        const remaining = ADDING_MIN_DURATION - (Date.now() - startedAt);
        if (remaining > 0) await wait(remaining);
        if (session !== addingSession) return;
        drawer.setAttribute("data-cart-adding", "done");
        await wait(ADDING_DONE_DURATION);
        clear();
      },
      /** Bij een fout: meteen weg, zodat de foutmelding niet achter de balk zit. */
      cancel: clear,

      /**
       * Gezet zodra /cart/add.js onderweg is. Faalt het daarna, dan komt de
       * foutmelding in de drawer te staan en moet hij dus open blijven.
       */
      claim() {
        claimed = true;
      },

      /**
       * Fout vóórdat de add begon (de configurator struikelt over zijn eigen
       * servercall): balk weg én de drawer weer dicht als wij hem openden. De
       * klant blijft zo bij de configurator, waar de foutmelding staat.
       */
      abort() {
        const closeDrawer = openedDrawer && !claimed && session === addingSession;
        clear();
        if (closeDrawer) HenkCart.close();
      },
    };

    activeAdding = handle;
    return handle;
  }

  /* ----------------------------------------------------------------------- */
  /* Publieke API                                                             */
  /* ----------------------------------------------------------------------- */

  const HenkCart = {
    /**
     * Hydrogen: CartForm.ACTIONS.LinesAdd.
     * `payload` mag een object ({ items: [...] }) of FormData zijn — FormData
     * is nodig voor file-properties (de configurator stuurt `_image` als
     * bestand mee).
     */
    async add(payload, { open = true, source = "add" } = {}) {
      clearError();

      // Openen gebeurt vóór de request, niet erna: de drawer vliegt direct
      // open en toont de laadstatus terwijl /cart/add.js nog bezig is.
      let adding = null;
      if (open) {
        const wasOpen = isDrawerOpen();
        HenkCart.open("add");
        adding = startAdding({ openedDrawer: !wasOpen });
        adding?.claim();
      }

      try {
        const data = await post(URLS.add, payload, source);
        // Bewust awaiten: de aanroeper (o.a. de configurator) houdt zijn eigen
        // knop in loading-state tot de drawer uitgespeeld is.
        await adding?.finish();
        return data;
      } catch (error) {
        adding?.cancel();
        throw error;
      }
    },

    /** items: [{ id, quantity, properties }] */
    addLines(items, options = {}) {
      return HenkCart.add({ items }, { source: "lines-add", ...options });
    },

    /** Add to cart vanaf een bestaand {% form 'product' %}. */
    addFromForm(form, options = {}) {
      return HenkCart.add(new FormData(form), { source: "form-add", ...options });
    },

    /** Add to cart vanaf een zelf opgebouwde FormData (configurators). */
    addFormData(formData, options = {}) {
      return HenkCart.add(formData, { source: "form-data-add", ...options });
    },

    /** Hydrogen: CartForm.ACTIONS.LinesUpdate */
    async updateLines(lines, { source = "lines-update" } = {}) {
      clearError();
      if (lines.length === 1) {
        const { key, quantity } = lines[0];
        return post(URLS.change, { id: key, quantity }, source);
      }
      const updates = {};
      for (const { key, quantity } of lines) updates[key] = quantity;
      return post(URLS.update, { updates }, source);
    },

    /** Hydrogen: CartForm.ACTIONS.LinesRemove */
    async removeLines(keys, { source = "lines-remove" } = {}) {
      return HenkCart.updateLines(
        keys.map((key) => ({ key, quantity: 0 })),
        { source },
      );
    },

    /**
     * Line item properties overschrijven. /cart/change.js vervangt álle
     * properties van een regel, dus de aanroeper moet de bestaande properties
     * meesturen. Gebruikt door de levertijd-ophaler hieronder.
     */
    async updateLineProperties(lineIndex, properties, { source = "line-properties", silent = false } = {}) {
      return post(URLS.change, { line: lineIndex, properties }, source, { silent });
    },

    /** Alleen de secties opnieuw ophalen, zonder mutatie. */
    async refresh({ open = false, source = "refresh" } = {}) {
      const ids = sectionIds();
      if (ids.length && window.HenkSections) {
        await window.HenkSections.render(ids, sectionsUrl());
      }
      const cart = await fetch(URLS.cart, { headers: { Accept: "application/json" } })
        .then((r) => r.json())
        .catch(() => null);
      document.dispatchEvent(
        new CustomEvent("cart:updated", { detail: { cart, source } }),
      );
      if (open) HenkCart.open("refresh");
      return cart;
    },

    /**
     * Opent de drawer en zet de laadstatus meteen aan, nog voordat er een
     * add-request is. Voor flows die eerst werk doen — de configurator doet
     * een servercall en maakt een screenshot — en pas daarna `addFormData()`
     * aanroepen: zonder dit staat de klant seconden naar een knop te kijken
     * voordat de drawer eindelijk openvliegt.
     *
     * Geeft een handle terug: `abort()` in je catch, `finish()` hoef je niet
     * aan te roepen — de `add()` die erna komt neemt de sessie over en speelt
     * hem zelf uit.
     */
    beginAdding() {
      clearError();
      const adding = startAdding({ openedDrawer: !isDrawerOpen() });

      // Staat de laadanimatie op 0 ms (sectie-instelling: "direct de nieuwe
      // winkelwagen tonen"), dan openen we niet vooruit: een drawer die seconden
      // met de óude inhoud blijft staan is verwarrender dan eentje die pas bij
      // de add openvliegt.
      if (adding) HenkCart.open("add");
      return adding;
    },

    open(reason) {
      document.querySelector("cart-drawer")?.open(reason);
    },

    close() {
      document.querySelector("cart-drawer")?.close();
    },

    toggle() {
      document.querySelector("cart-drawer")?.toggle();
    },
  };

  window.HenkCart = HenkCart;

  /* ----------------------------------------------------------------------- */
  /* <cart-drawer> — Hydrogen's <Aside type="cart">                           */
  /* ----------------------------------------------------------------------- */

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.isOpen = false;
      this.returnFocusTo = null;

      // Gedelegeerd: de inhoud wordt bij elke section render vervangen, de
      // listeners op dit element blijven daardoor geldig.
      this.addEventListener("click", (event) => {
        if (event.target.closest("[data-cart-drawer-close]")) {
          event.preventDefault();
          this.close();
        }
      });

      // Na een section render is het element dat focus had vervangen. Zonder
      // dit valt de focus terug op <body> en werkt toetsenbordnavigatie in de
      // open drawer niet meer.
      this.addEventListener("section:rendered", () => {
        if (this.isOpen && document.activeElement === document.body) {
          this.querySelector("button[data-cart-drawer-close]")?.focus();
        }
      });

      this._onKeydown = (event) => {
        if (!this.isOpen) return;
        // Een <dialog> (de cadeau-modal) ligt in de top layer bóven de drawer
        // en regelt Escape en focus zelf. Zou de drawer daar overheen gaan, dan
        // sluit Escape allebei tegelijk en trekt de focus trap de focus uit de
        // modal terug de drawer in.
        if (document.querySelector("dialog[open]")) return;
        if (event.key === "Escape") {
          event.preventDefault();
          this.close();
        } else if (event.key === "Tab") {
          this._trapFocus(event);
        }
      };
      document.addEventListener("keydown", this._onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener("keydown", this._onKeydown);
    }

    open(reason = "manual") {
      if (this.isOpen) return;
      this.isOpen = true;
      this.returnFocusTo = document.activeElement;
      this.setAttribute("aria-hidden", "false");
      this.classList.add("is-open");
      document.documentElement.classList.add("cart-drawer-is-open");
      document.dispatchEvent(new CustomEvent("cart:opened", { detail: { reason } }));
      document
        .querySelectorAll("[data-cart-drawer-toggle]")
        .forEach((el) => el.setAttribute("aria-expanded", "true"));

      // De drawer stond tot zojuist op visibility: hidden. Zolang de browser de
      // style niet opnieuw berekend heeft, geldt de knop als niet-gerenderd en
      // negeert focus() hem — vandaar de geforceerde reflow.
      // Let op: de overlay draagt óók data-cart-drawer-close, dus expliciet de
      // knop pakken; een <div> focussen doet niets.
      void this.offsetWidth;
      this.querySelector("button[data-cart-drawer-close]")?.focus();
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.setAttribute("aria-hidden", "true");
      this.classList.remove("is-open");
      document.documentElement.classList.remove("cart-drawer-is-open");
      document
        .querySelectorAll("[data-cart-drawer-toggle]")
        .forEach((el) => el.setAttribute("aria-expanded", "false"));

      // Het cart-icoon wordt bij elke mutatie opnieuw gerenderd, dus het
      // oorspronkelijke element kan losgekoppeld zijn. Dan focussen we de
      // verse toggle op dezelfde plek.
      const previous = this.returnFocusTo;
      const usable =
        previous &&
        previous !== document.body &&
        document.contains(previous) &&
        !this.contains(previous);
      const target = usable ? previous : document.querySelector("[data-cart-drawer-toggle]");
      target?.focus();
      this.returnFocusTo = null;
    }

    toggle() {
      this.isOpen ? this.close() : this.open();
    }

    _trapFocus(event) {
      const items = [...this.querySelectorAll(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null,
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get("cart-drawer")) {
    customElements.define("cart-drawer", CartDrawer);
  }

  /* ----------------------------------------------------------------------- */
  /* Gedelegeerde triggers                                                    */
  /* ----------------------------------------------------------------------- */

  // Cart-icoon in de header opent de drawer i.p.v. naar /cart te navigeren.
  // De drawer staat op elke pagina; ontbreekt hij toch, dan blijft de link naar
  // /cart werken — die stuurt door naar de homepage met ?cart=true.
  document.addEventListener("click", (event) => {
    const toggle = event.target.closest("[data-cart-drawer-toggle]");
    if (!toggle) return;
    const drawer = document.querySelector("cart-drawer");
    if (!drawer) return;
    event.preventDefault();
    drawer.toggle();
  });

  // Aantal wijzigen. Zonder JS is dit een gewone link naar /cart/change.
  const pendingQuantity = new Map();

  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-cart-quantity-change]");
    if (!button) return;
    event.preventDefault();

    if (button.getAttribute("aria-disabled") === "true") return;

    const line = button.closest("[data-cart-line]");
    const key = button.dataset.key;
    if (!line || !key) return;

    const delta = parseInt(button.dataset.quantityDelta || "0", 10);
    const current = parseInt(line.dataset.quantity || "1", 10);
    const next = Math.max(1, current + delta);
    if (next === current) return;

    setOptimisticQuantity(key, next);
    setLineBusy(key, true);

    // Snel achter elkaar klikken mag elkaar niet inhalen: alleen de laatste
    // waarde gaat naar de server (Hydrogen doet dit met fetcherKey).
    clearTimeout(pendingQuantity.get(key));
    pendingQuantity.set(
      key,
      setTimeout(() => {
        pendingQuantity.delete(key);
        HenkCart.updateLines([{ key, quantity: next }], { source: "quantity" }).catch(
          () => setLineBusy(key, false),
        );
      }, 300),
    );
  });

  // Regel verwijderen. Zonder JS is dit item.url_to_remove.
  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-cart-remove]");
    if (!button) return;
    event.preventDefault();

    const key = button.dataset.key;
    if (!key) return;

    lineByKey(key)?.setAttribute("data-removing", "");
    setLineBusy(key, true);
    HenkCart.removeLines([key], { source: "remove" }).catch(() => {
      lineByKey(key)?.removeAttribute("data-removing");
      setLineBusy(key, false);
    });
  });

  // Elk add-to-cart formulier op de site (PDP, sofa-fallback, product cards).
  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.hasAttribute("data-cart-add-skip")) return;

    const action = form.getAttribute("action") || "";
    if (!action.includes("/cart/add")) return;

    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    button?.setAttribute("aria-busy", "true");

    HenkCart.addFromForm(form, { source: "product-form" })
      .catch(() => {}) // de foutmelding is al getoond in post()
      .finally(() => {
        button?.removeAttribute("aria-busy");
        // henk-button-loading.min.js zet data-state="loading" bij de click; die
        // moet weer weg nu we niet meer navigeren.
        if (button && button.dataset.state === "loading") delete button.dataset.state;
      });
  });

  /* ----------------------------------------------------------------------- */
  /* Verwachte levertijd                                                      */
  /* ----------------------------------------------------------------------- */

  /**
   * Regels met een `_ArticleCodes` property krijgen hun levertijd van een
   * extern endpoint. De opgehaalde datum wordt als `_delivery_date` property
   * op de regel gezet, zodat hij daarna server-side gerenderd wordt
   * (snippets/cart-delivery-time.liquid).
   *
   * Voorheen werd hierna `window.location.reload()` aangeroepen omdat het
   * wijzigen van properties de line item key verandert. Dat is niet meer nodig:
   * /cart/change.js levert de nieuwe sectie-HTML mee.
   */
  const deliveryWeek = (() => {
    const failed = new Set();
    let scanning = false;
    let queued = false;

    const T = CONFIG.strings;

    function formatLeadTime(iso) {
      if (!iso) return "";
      const target = new Date(`${iso}T00:00:00`);
      if (Number.isNaN(target.getTime())) return "";
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const days = Math.max(0, Math.ceil((target.getTime() - today.getTime()) / 86400000));
      if (days <= 0) return T.leadImmediate;
      if (days <= 14) return days === 1 ? `1 ${T.unitDay}` : `${days} ${T.unitDays}`;
      const weeks = Math.round(days / 7);
      return weeks === 1 ? `1 ${T.unitWeek}` : `${weeks} ${T.unitWeeks}`;
    }

    async function fetchCart() {
      const res = await fetch(URLS.cart, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`cart fetch ${res.status}`);
      return res.json();
    }

    /**
     * Op /cart staat iedere regel twee keer in de DOM: op de pagina zelf én in
     * de drawer. Beide kopieën moeten dezelfde levertijd tonen, maar hem samen
     * maar één keer ophalen — vandaar dat we per `data-line-index` werken.
     */
    function lineTwins(el) {
      const index = (el.dataset.lineIndex || "").trim();
      if (!index) return [el];
      return [
        ...document.querySelectorAll(
          `[data-cart-line][data-line-index="${CSS.escape(index)}"]`,
        ),
      ];
    }

    async function updateItem(el, { force } = {}) {
      if (!CONFIG.deliveryWeekEndpoint) return false;

      const items = lineTwins(el);
      if (items.some((item) => item.dataset.deliveryWeekBusy === "1")) return false;
      items.forEach((item) => (item.dataset.deliveryWeekBusy = "1"));

      const displays = items
        .map((item) => item.querySelector("[data-delivery-week-display]"))
        .filter(Boolean);
      const buttons = items
        .map((item) => item.querySelector("[data-delivery-week-refresh]"))
        .filter(Boolean);
      const codes = (el.dataset.articleCodes || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      if (!codes.length) {
        items.forEach((item) => delete item.dataset.deliveryWeekBusy);
        return false;
      }

      const codeKey = codes.join(",");

      displays.forEach((display) => {
        display.removeAttribute("data-error");
        display.setAttribute("data-loading", "");
        display.textContent = T.deliveryLoading;
      });
      buttons.forEach((btn) => (btn.disabled = true));

      try {
        const cart = await fetchCart();
        const lineIdx = parseInt(el.dataset.lineIndex, 10);
        const line = cart.items?.[lineIdx - 1];
        if (!line) throw new Error("line not found");

        if (!force && line.properties && line.properties._delivery_date) {
          const date = line.properties._delivery_date;
          displays.forEach((display) => {
            display.removeAttribute("data-loading");
            display.textContent = `${T.deliveryLabel}: ${formatLeadTime(date)}`;
          });
          buttons.forEach((btn) => btn.remove());
          items.forEach((item) => (item.dataset.deliveryDate = date));
          return false;
        }

        const res = await fetch(CONFIG.deliveryWeekEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ articleCodes: codes }),
        });
        if (!res.ok) throw new Error(`endpoint ${res.status}`);
        const data = await res.json();
        const date = data.deliveryDate || "";
        if (!date) throw new Error("empty deliveryDate");

        // /cart/change.js vervangt álle properties en accepteert alleen
        // strings. File-upload properties (bv. _image = { url, size, ... })
        // coercen naar hun URL, anders gaan ze verloren.
        const merged = { ...(line.properties || {}), _delivery_date: date };
        const safeProps = {};
        for (const [k, v] of Object.entries(merged)) {
          if (v == null) continue;
          if (typeof v === "string") safeProps[k] = v;
          else if (typeof v === "object" && typeof v.url === "string") safeProps[k] = v.url;
          else safeProps[k] = String(v);
        }

        failed.delete(codeKey);
        // Levert meteen de nieuwe sectie-HTML op; de regel rendert daarna
        // server-side met de juiste levertijd.
        await HenkCart.updateLineProperties(lineIdx, safeProps, {
          source: "delivery-week",
          silent: true,
        });
        return true;
      } catch (err) {
        console.error("[delivery-week]", err);
        failed.add(codeKey);
        displays.forEach((display) => {
          display.removeAttribute("data-loading");
          display.setAttribute("data-error", "");
          display.textContent = T.deliveryUnknown;
        });
        buttons.forEach((btn) => (btn.disabled = false));
        return false;
      } finally {
        items.forEach((item) => delete item.dataset.deliveryWeekBusy);
      }
    }

    /**
     * Zoek regels zonder levertijd en haal die op. Draait na elke render, dus
     * ook voor regels die net via de drawer zijn toegevoegd.
     */
    async function scan() {
      if (scanning) {
        queued = true;
        return;
      }
      scanning = true;
      let updatedAny = false;

      try {
        // Op /cart komt elke regel twee keer langs (pagina + drawer); alleen de
        // eerste kopie doet de aanvraag, updateItem() werkt beide bij.
        const seen = new Set();
        const pending = [...document.querySelectorAll("[data-cart-line]")].filter((el) => {
          const codes = (el.dataset.articleCodes || "").trim();
          if (!codes) return false;
          if ((el.dataset.deliveryDate || "").trim()) return false;
          if (failed.has(codes.split(",").map((s) => s.trim()).filter(Boolean).join(","))) {
            return false;
          }
          const index = (el.dataset.lineIndex || "").trim();
          if (index) {
            if (seen.has(index)) return false;
            seen.add(index);
          }
          return true;
        });

        for (const el of pending) {
          // Een geslaagde update rendert de secties opnieuw; `el` en zijn
          // buren zijn dan losgekoppeld, dus we stoppen en scannen zo meteen
          // opnieuw op de verse DOM.
          if (await updateItem(el)) {
            updatedAny = true;
            break;
          }
        }
      } finally {
        scanning = false;
      }

      // Elke ronde zet er hoogstens één regel op `_delivery_date`; zolang er
      // nog regels open staan gaan we door. Regels die falen komen in `failed`
      // en worden niet opnieuw geprobeerd, dus dit termineert altijd.
      if (updatedAny || queued) {
        queued = false;
        setTimeout(scan, 0);
      }
    }

    document.addEventListener("click", (event) => {
      const btn = event.target.closest("[data-delivery-week-refresh]");
      if (!btn) return;
      event.preventDefault();
      const line = btn.closest("[data-cart-line]");
      if (line) updateItem(line, { force: true });
    });

    return { scan };
  })();

  /* ----------------------------------------------------------------------- */
  /* Init                                                                     */
  /* ----------------------------------------------------------------------- */

  function init() {
    deliveryWeek.scan();
  }

  /**
   * `?cart=true` opent de drawer meteen bij het laden van de pagina. Werkt op
   * elke pagina; templates/cart.liquid gebruikt het om /cart naar de homepage
   * door te sturen met de drawer open. Ook handig voor links vanuit
   * mails/campagnes en voor terugkeren uit checkout. `?cart=false` (of 0) doet
   * niets, zodat de parameter ook uitgezet kan worden zonder hem weg te laten.
   */
  function openFromUrl() {
    const value = new URLSearchParams(window.location.search).get("cart");
    if (value === null) return;
    if (["", "true", "1", "open"].includes(value.trim().toLowerCase())) {
      HenkCart.open("url");
    }
  }

  function start() {
    init();
    openFromUrl();
  }

  document.addEventListener("cart:updated", init);
  document.addEventListener("shopify:section:load", (event) => {
    if (event.detail?.sectionId?.includes("cart")) init();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
  document.addEventListener("cart:opened", async (e) => {
  try {
    const cart = await fetch(URLS.cart, { headers: { Accept: "application/json" } }).then((r) => r.json());
    window.posthog?.capture("cart_viewed", {
      source: "cart_drawer",
      open_reason: e.detail?.reason,
      value: cart.total_price / 100,
      currency: cart.currency,
      item_count: cart.item_count,
      items: cart.items.map((i) => ({
        product_id: String(i.product_id), variant_id: String(i.variant_id), sku: i.sku,
        name: i.product_title, price: i.final_price / 100, quantity: i.quantity,
      })),
    });
  } catch {}
});
})();
